import { Check } from "@phosphor-icons/react";
import clsx from "clsx";
import { useMemo } from "react";

import RenderIf from "common/components/RenderIf";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Question, QuestionnaireResult } from "common/types";

import type { FieldError } from "react-hook-form";

interface QuestionDisplayProps {
  question: Question;
  currentResponse: QuestionnaireResult;
  handleAnswer: (
    answer: string,
    isCustomAnswer: boolean,
    isDemo: boolean,
  ) => void;
  handleCustomInputChange: (value: string) => void;
  errorMessage?: FieldError;
}

// Shuffle algorithm
function shuffleArray<T>(array: T[]) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function QuestionDisplay({
  question,
  currentResponse,
  handleAnswer,
  handleCustomInputChange,
  errorMessage = null,
}: QuestionDisplayProps) {
  const isTabletOrDesktop = useTwBreakpoint("md");
  const {
    id: questionId,
    title,
    type,
    answers,
    is_shuffled: isShuffled,
  } = question;

  // Process answers: shuffle answers if isShuffled is true, keep custom answers at the end
  const processedAnswers = useMemo(() => {
    if (!isShuffled) {
      return answers;
    }

    const regularAnswers = answers.filter((answer) => !answer.isCustomAnswer);
    const customAnswers = answers.filter((answer) => answer.isCustomAnswer);

    const shuffledAnswers = shuffleArray(regularAnswers);

    return [...shuffledAnswers, ...customAnswers];
  }, [questionId, answers, isShuffled]);

  // Find the placeholder text from the currently selected custom answer(s)
  const selectedCustomAnswer = processedAnswers.find(
    (answer) =>
      answer.isCustomAnswer && currentResponse?.answers.includes(answer.title),
  );

  const placeholderText =
    selectedCustomAnswer?.placeholderText || "Type here...";

  return (
    <div className="flex w-full flex-col items-center justify-center gap-4">
      <h1 className="text-center text-2xl-bold">{title}</h1>

      <div className="flex w-full flex-col flex-wrap items-center justify-center gap-2 md:flex-row md:gap-4">
        {processedAnswers.map((answer) => {
          const {
            id,
            title: answerTitle,
            isCustomAnswer,
            demo_call: isDemo,
          } = answer;
          const isChecked = currentResponse?.answers.includes(answerTitle);

          return (
            <button
              key={`${id}-${answerTitle}`}
              className={clsx([
                "flex w-full items-center gap-2 rounded-3xl px-4 py-3 md:w-auto text-button-sm",
                isChecked
                  ? "bg-purple-500 text-white shadow-sm"
                  : "ring-1 ring-inset ring-black-200 transition-shadow duration-300 ease-in-out hover:shadow-sm",
              ])}
              type="button"
              onClick={() => handleAnswer(answerTitle, isCustomAnswer, isDemo)}
            >
              <RenderIf condition={type === "multiple"}>
                {/* Custom checkbox */}
                <span
                  className={clsx([
                    "flex size-3.5 items-center justify-center rounded ring-[1.25px] ring-inset",
                    isChecked
                      ? "bg-white text-purple-600 ring-white"
                      : "ring-black-950",
                  ])}
                >
                  <RenderIf condition={isChecked}>
                    <Check size={10} weight="bold" />
                  </RenderIf>
                </span>
              </RenderIf>

              {answerTitle}
            </button>
          );
        })}
      </div>

      <RenderIf condition={currentResponse?.additional_info !== undefined}>
        <Input
          className="my-2 w-full"
          hasError={!!errorMessage?.message}
          variant={isTabletOrDesktop ? "md" : "lg"}
          placeholder={placeholderText}
          value={currentResponse?.additional_info}
          onChange={(e) => handleCustomInputChange(e.target.value)}
        />
      </RenderIf>
    </div>
  );
}
